import { AccountsTableWide } from "./AccountsTableWide";
import { DmLoadingSpinner } from "../../../manager/utils/functions";
import { AccountsTableNarrow } from "./AccountsTableNarrow";
import { useSubscription } from "../../utils/useSubscription";
import { useLearnerContext } from "../../contexts/LearnerContext";
import { unreachableCase } from "../../../utils";
import { canModifySubscription } from "../../utils/canModifySubscription";
import { AddLearnerButton } from "./AddLearnerButton";
import { getOpenSeats } from "../../utils/getOpenSeats";
import { useParentContext } from "../../contexts/ParentContext";
import { ReactivateLink } from "./ReactivateLink";
import Button from "../../../student/components/generic/button";
import { Learner } from "../../types";
import { PARENT_ADD_LEARNER_LINK } from "../../constants";
import { isSubscriptionActive } from "../../utils/isSubscriptionActive";

const AccountsTableLoading = () => {
  return (
    <div className="flex h-52 w-full items-center justify-center">
      <DmLoadingSpinner message="" />
    </div>
  );
};

const AccountsTableForSubscription = () => {
  const { subscription, plan } = useSubscription();
  const { learners } = useParentContext();

  const canModify = subscription ? canModifySubscription(subscription) : false;
  const openSeats = subscription
    ? getOpenSeats(subscription.quantity, learners)
    : 0;
  const getReactivateLink = (learner: Learner) =>
    subscription ? (
      <ReactivateLink subscription={subscription} learner={learner} />
    ) : null;

  if (!subscription || !plan) {
    return <AccountsTableLoading />;
  }

  return (
    <>
      <AccountsTableNarrow
        openSeats={openSeats}
        canModify={canModify}
        getReactivateLink={getReactivateLink}
        footerAddLearnerButton={
          <AddLearnerButton
            className={openSeats <= 0 ? "mt-8" : ""}
            subscription={subscription}
            plan={plan}
            outline={openSeats > 0}
          />
        }
      />
      <AccountsTableWide
        openSeats={openSeats}
        canModify={canModify}
        getReactivateLink={getReactivateLink}
        footerAddLearnerButton={
          <AddLearnerButton
            subscription={subscription}
            plan={plan}
            outline={openSeats > 0}
          />
        }
      />
    </>
  );
};

const AccountsTableForLicense = () => {
  const learnerContext = useLearnerContext();
  const { learners } = useParentContext();

  const openSeats = isSubscriptionActive(learnerContext.learner)
    ? getOpenSeats(learnerContext.learner?.subscriptionQuantity || 0, learners)
    : 0;
  // Can't cancel a license, so no need for a reactivate link
  const getReactivateLink = () => null;

  return (
    <>
      <AccountsTableNarrow
        openSeats={openSeats}
        canModify={openSeats > 0}
        getReactivateLink={getReactivateLink}
        footerAddLearnerButton={
          <Button
            className="flex h-10 items-center justify-center gap-2 p-0"
            type="outline"
            href={PARENT_ADD_LEARNER_LINK}
          >
            <i className="far fa-plus-circle text-xl" aria-hidden />
            Add New Learner
          </Button>
        }
      />
      <AccountsTableWide
        openSeats={openSeats}
        canModify={openSeats > 0}
        getReactivateLink={getReactivateLink}
        footerAddLearnerButton={
          <Button
            className="flex h-10 items-center justify-center gap-2 p-0"
            type="outline"
            href={PARENT_ADD_LEARNER_LINK}
          >
            <i className="far fa-plus-circle text-xl" aria-hidden />
            Add New Learner
          </Button>
        }
      />
    </>
  );
};

export const AccountsTable: React.FC = () => {
  const learnerContext = useLearnerContext();

  if (!learnerContext.learner) {
    return <AccountsTableLoading />;
  }

  switch (learnerContext.learner?.accessMethod) {
    case "subscription":
      return <AccountsTableForSubscription />;
    case "license":
      return <AccountsTableForLicense />;
    case undefined:
      return null;
    default:
      unreachableCase(learnerContext.learner?.accessMethod);
  }
};
